/**
 * Capability Definitions
 *
 * Capabilities are a "ceiling" layer above RBAC. Even if a role grants a permission,
 * if the account's plan/tier lacks the required capability, the action is blocked.
 *
 * This is used to gate entire feature areas based on what an instance has paid for,
 * while RBAC controls what individual users can do within those feature areas.
 *
 * PD-16917 (owner decisions 2026-10-08): the list is a strict whitelist. An
 * instance holds exactly the capabilities granted to it through its tiers and
 * one-off rows; an instance with no rows holds nothing, and a capability
 * marked inactive in the catalog is held by nobody.
 */

/**
 * Machine-readable capability aliases.
 * These must match the `alias` column in the `_capability` table in the DVP/login DB.
 */
export const Capabilities = {
	CRM_CORE: 'crm_core',
	SALES: 'sales',
	INVENTORY: 'inventory',
	/** Labeled "Delivery management": routes, trips, dispatch, drivers, vehicle list. */
	DISTRIBUTION: 'distribution',
	/** DOT pre/post-trip inspections, the vehicle map and Motive telematics. */
	FLEET: 'fleet',
	/** Merchandisers, sensors, module faults, firmware and equipment orders. */
	EQUIPMENT: 'equipment',
	/** Service orders, service trips and technician work. */
	SERVICE: 'service',
	/** Sales routes, sales trips and named locations. */
	FIELD_SALES: 'field_sales',
	/** Email marketing campaigns. */
	MARKETING: 'marketing',
	/** Public websites. */
	WEB: 'web',
	/** Promotions and the promotion engine. */
	PROMOTIONS: 'promotions',
	AI_AGENTS: 'ai_agents',
	REPORTING: 'reporting',
	/** Labeled "License management": the license registry and every licensing job. */
	COMPLIANCE: 'compliance',
	/**
	 * NPI/NCPDP/NDC/DEA-schedule fields and the State Board, DEA and
	 * controlled-substance order checks. Replaces `features.pharmacy_sales`.
	 */
	PHARMACY: 'pharmacy',
	AUTOMATION: 'automation',
	EMAIL: 'email',
	/** Ecommerce users and storefront sites (Brands & storefronts, designs). */
	ECOMMERCE: 'ecommerce',
	GOALS: 'goals',
	/** Calls, call history, AI phone assignments and click-to-dial integrations. */
	PHONE: 'phone',
	/** Texting from the inbox, Send SMS, SMS agents and text sender numbers. */
	SMS: 'sms',
	/**
	 * The master switch for the in-browser phone system. It replaces the
	 * on/off role of `softphone.twilioFeatureEnabled`.
	 */
	SOFTPHONE: 'softphone',
	/**
	 * PD-16765: the Claude connector (public MCP endpoint). An add-on that
	 * belongs to no tier, so Workd grants it to an instance one at a time.
	 */
	AI_MCP: 'ai_mcp',
	/**
	 * Campaigns: Marketing email campaigns (sent through Workd's shared SendGrid
	 * account) and adding contacts to campaigns. An add-on held by no tier and
	 * granted to Auvis staging only (owner decision 2026-10-14) until bulk-send
	 * limits, unsubscribe handling and the campaign plan are in place: one
	 * tenant's bulk send can get the shared SendGrid account blocked for all.
	 */
	CAMPAIGNS: 'campaigns',
	/**
	 * Jobs and projects: the won work delivered to a customer (job) and the
	 * real-world construction project several customers may bid on (project).
	 * Not every tenant tracks jobs, so it is an add-on held by no tier and
	 * granted per tenant (Macomb on, others off until they ask; owner decision
	 * 2026-10-14). Without it opportunities work exactly as before.
	 */
	JOBS: 'jobs',
} as const;

export type CapabilityAlias = (typeof Capabilities)[keyof typeof Capabilities];

/**
 * The user-facing name of the Lead Finder screen (PD-13950, owner decision
 * 2026-10-08). Internal identifiers (the `lead_finder` RBAC prefix, routes and
 * module names) keep the old name; only what a user reads changes, so the
 * name lives in one constant and is easy to change again.
 */
export const LeadFinderDisplayName = 'Licensed Facility Finder';

/**
 * Forms, form submissions and the media library are shared by the marketing,
 * website and storefront areas, so any one of the three opens them.
 */
export const FormsAndMediaCapabilities: ReadonlyArray<CapabilityAlias> = [
	Capabilities.MARKETING,
	Capabilities.WEB,
	Capabilities.ECOMMERCE,
];

/**
 * Maps RBAC action prefixes to required capabilities.
 *
 * When the auth guard loads RBAC permissions, it filters out any actions whose
 * prefix maps to a capability the tenant lacks. This means existing @RequireRbac()
 * decorators automatically enforce capabilities without per-endpoint changes.
 *
 * The key is the first segment of the RBAC action string (before the first colon).
 * For example, 'clients:read' has prefix 'clients', which requires CRM_CORE.
 * A key can also name a longer prefix ('integrations:mcp'). The longest
 * matching prefix wins, so one sub-area of a top-level group can need its own
 * capability while the rest of the group stays ungated. A list value means
 * ANY of its capabilities opens the action.
 *
 * Actions whose prefix is NOT in this map are treated as core/system-level and
 * are allowed through regardless of capabilities (e.g., settings, user, rbac,
 * announcements, automation).
 *
 * NOTE: Automation is intentionally unmapped — it runs background tasks (email
 * steps, scheduled jobs, etc.) that tenants depend on regardless of their tier.
 * Gating it could silently break internal processes.
 *
 * Fleet has no entry: inspections run on `trips:*` actions and the vehicle map
 * and Motive endpoints have no action of their own, so fleet is enforced with
 * `@Security.RequireCapability` instead.
 */
export const ActionCapabilityMap: Record<
	string,
	CapabilityAlias | ReadonlyArray<CapabilityAlias>
> = {
	// CRM Core — clients, tasks, issues, teams, groups
	clients: Capabilities.CRM_CORE,
	tasks: Capabilities.CRM_CORE,
	issues: Capabilities.CRM_CORE,
	groups: Capabilities.CRM_CORE,
	teams: Capabilities.CRM_CORE,
	// The client Distribution tab edits delivery settings.
	'clients:distribution': Capabilities.DISTRIBUTION,
	// Client license records belong to pharmacy sales and are read by
	// license management, so either one opens them.
	'clients:licenses': [Capabilities.PHARMACY, Capabilities.COMPLIANCE],

	// Sales — quotes, orders, invoices, transactions, opportunities, price contracts
	quotes: Capabilities.SALES,
	orders: Capabilities.SALES,
	invoices: Capabilities.SALES,
	transactions: Capabilities.SALES,
	opportunities: Capabilities.SALES,
	price_contracts: Capabilities.SALES,
	// Jobs and projects are their own add-on (2026-10-14), so the jobs:* and
	// projects:* permissions disappear at login where it is off.
	jobs: Capabilities.JOBS,
	projects: Capabilities.JOBS,

	// Inventory — items, warehouses, locations, vendors, purchase orders, sourcing
	items: Capabilities.INVENTORY,
	warehouses: Capabilities.INVENTORY,
	locations: Capabilities.INVENTORY,
	vendors: Capabilities.INVENTORY,
	purchase_orders: Capabilities.INVENTORY,
	sourcing_requests: Capabilities.INVENTORY,

	// Delivery management — routes (incl. drivers and vehicles), trips,
	// replenishment, the Schedule and dispatch settings. Service trips run on
	// the trip endpoints and service orders sit on the Schedule, so those keys
	// also open for service; equipment orders are scheduled there too.
	routes: Capabilities.DISTRIBUTION,
	trips: [Capabilities.DISTRIBUTION, Capabilities.SERVICE],
	distribution: Capabilities.DISTRIBUTION,
	'distribution:schedule': [
		Capabilities.DISTRIBUTION,
		Capabilities.SERVICE,
		Capabilities.EQUIPMENT,
	],
	'settings:distribution_config': Capabilities.DISTRIBUTION,

	// Equipment management. `sensors:ingest` and `sensors:ota` stay unmapped:
	// they carry device traffic, and dropping it would lose readings.
	merchandisers: Capabilities.EQUIPMENT,
	'sensors:create': Capabilities.EQUIPMENT,
	'sensors:configure': Capabilities.EQUIPMENT,
	'settings:sensor_firmware': Capabilities.EQUIPMENT,
	'distribution:equipment_orders': Capabilities.EQUIPMENT,

	// Service management
	service_orders: Capabilities.SERVICE,
	service_trips: Capabilities.SERVICE,

	// Field sales routing
	sales_routes: Capabilities.FIELD_SALES,
	sales_trips: Capabilities.FIELD_SALES,
	named_locations: Capabilities.FIELD_SALES,
	'reporting:sales_routes': Capabilities.FIELD_SALES,

	// Marketing, websites and promotions are separate capabilities.
	marketing: Capabilities.MARKETING,
	// Campaigns (email sends and campaign membership) need their own add-on;
	// the rest of marketing (forms, media) keeps the marketing capability.
	'marketing:campaigns': Capabilities.CAMPAIGNS,
	websites: Capabilities.WEB,
	promos: Capabilities.PROMOTIONS,
	'reporting:sales:promo_redemptions': Capabilities.PROMOTIONS,
	// PD-17136 — the job reports exist only where the tenant tracks jobs.
	'reporting:sales:sales_by_job': Capabilities.JOBS,
	'reporting:sales:open_orders_by_job': Capabilities.JOBS,

	// Forms, submissions, the media library and the forms & privacy settings
	// open with ANY of marketing, web or ecommerce. `public_website:*` stays
	// ungated: it carries public posts.
	forms: FormsAndMediaCapabilities,
	form_submissions: FormsAndMediaCapabilities,
	media: FormsAndMediaCapabilities,
	'settings:web': FormsAndMediaCapabilities,

	// AI Agents — agent setup, conversations, usage, monitoring
	ai_agents: Capabilities.AI_AGENTS,

	// Reporting
	reporting: Capabilities.REPORTING,

	// License management — licenses
	licenses: Capabilities.COMPLIANCE,
	// Lead Finder (PD-16825) is built on the license registry and is offered
	// only to the tenants that use Workd's license verification (Chris, 2026-10-04).
	lead_finder: Capabilities.COMPLIANCE,

	// Email — mailbox, templates
	email: Capabilities.EMAIL,

	// Ecommerce — ecommerce users and storefront sites
	ecommerce_users: Capabilities.ECOMMERCE,
	portal_sites: Capabilities.ECOMMERCE,
	'settings:storefront_designs': Capabilities.ECOMMERCE,
	registration_fields: Capabilities.ECOMMERCE,
	// The Brand page (Brands & storefronts) holds a brand's look and its
	// Websites tab as well as its storefront, so a website-only tenant opens
	// it too; the storefront tabs stay behind the ecommerce actions above.
	'settings:brands_storefronts': [Capabilities.ECOMMERCE, Capabilities.WEB],
	'settings:brands': [Capabilities.ECOMMERCE, Capabilities.WEB],

	// Goals
	goals: Capabilities.GOALS,

	// Phone — calls, call history, phone management. `phone:calls:read` (AI
	// conversation recordings) stays here.
	call_history: Capabilities.PHONE,
	'call-history': Capabilities.PHONE,
	phone: Capabilities.PHONE,
	// `phone:system:configure` sets up the tenant's numbers: which number
	// sends texts (Text Messages), and which numbers AI voice and SMS agents
	// answer. A tenant with SMS but no phone still configures its text
	// numbers, so either capability opens it.
	'phone:system': [Capabilities.PHONE, Capabilities.SMS],

	// SMS is separate from phone (10DLC cost). System texts (sign-in codes,
	// delivery and statement notices) send without any action and stay ungated.
	'phone:sms': Capabilities.SMS,

	// Softphone — coaching and live monitoring belong to the in-browser phone.
	// The communications inbox carries both softphone calls and texts, so
	// either capability opens it.
	'phone:coaching': Capabilities.SOFTPHONE,
	communications: [Capabilities.SOFTPHONE, Capabilities.SMS],

	// Claude connector (MCP). Only this sub-area: integrations:client_item:*
	// stays ungated.
	'integrations:mcp': Capabilities.AI_MCP,

	// PD-16876: `ai:usage` (the usage screen) and `ai:credits` (the Google
	// search share) are deliberately NOT gated. The screen now also carries
	// the monthly Google Maps credit pool, which applies to every tenant,
	// AI capability or not; the AI-specific parts of the page check the
	// capabilities themselves. (PD-16760 had gated `ai:usage` on ai_agents OR
	// ai_mcp; a list value here still means ANY of its capabilities.)
};

/**
 * Capabilities that belong to no tier. Workd grants them to an instance one
 * at a time (an `_instance__capability` row with no tier), so a plan change
 * never turns one on by accident.
 *
 * PD-16917: compliance moved here from the standard and premium tiers, so
 * license management runs only where Workd turns it on.
 */
export const AddOnCapabilities: ReadonlyArray<CapabilityAlias> = [
	Capabilities.AI_MCP,
	Capabilities.CAMPAIGNS,
	Capabilities.JOBS,
	Capabilities.COMPLIANCE,
	Capabilities.PHARMACY,
	Capabilities.SOFTPHONE,
	Capabilities.EQUIPMENT,
	Capabilities.SERVICE,
	Capabilities.PROMOTIONS,
];

/**
 * The plan capabilities: everything that is not an add-on. An instance that
 * had no capability rows before PD-16917 could reach all of these.
 */
export const PlanCapabilities: ReadonlyArray<CapabilityAlias> = Object.values(
	Capabilities,
).filter((alias) => !AddOnCapabilities.includes(alias));

/** How config-web groups the capability list. */
export type CapabilityGroupModel =
	| 'Core'
	| 'Sales and inventory'
	| 'Operations'
	| 'Marketing and commerce'
	| 'Communications'
	| 'Compliance'
	| 'AI';

export interface CapabilityDefinitionModel {
	alias: CapabilityAlias;
	label: string;
	description: string;
	group: CapabilityGroupModel;
	/**
	 * Capabilities this one is not useful without. config-web warns when one
	 * is missing; nothing enforces it.
	 */
	dependsOn: ReadonlyArray<CapabilityAlias>;
}

/**
 * Labels, descriptions and grouping for every capability, in display order.
 * The `_capability` rows carry the same label and description (written by the
 * RemapPlanCapabilities core migration); this copy is what config-web groups
 * by and what new code reads.
 */
export const CapabilityDefinitions: ReadonlyArray<CapabilityDefinitionModel> = [
	{
		alias: Capabilities.CRM_CORE,
		label: 'CRM Core',
		description: 'Clients, contacts, notes, tasks, issues, teams, groups',
		group: 'Core',
		dependsOn: [],
	},
	{
		alias: Capabilities.REPORTING,
		label: 'Reporting',
		description: 'Reporting module',
		group: 'Core',
		dependsOn: [],
	},
	{
		alias: Capabilities.AUTOMATION,
		label: 'Automation',
		description: 'Automation flows, triggers',
		group: 'Core',
		dependsOn: [],
	},
	{
		alias: Capabilities.GOALS,
		label: 'Goals',
		description: 'Goals',
		group: 'Core',
		dependsOn: [],
	},
	{
		alias: Capabilities.SALES,
		label: 'Sales',
		description: 'Quotes, orders, invoices, transactions, opportunities',
		group: 'Sales and inventory',
		dependsOn: [],
	},
	{
		alias: Capabilities.INVENTORY,
		label: 'Inventory',
		description:
			'Items, warehouses, locations, vendors, purchase orders, sourcing',
		group: 'Sales and inventory',
		dependsOn: [],
	},
	{
		alias: Capabilities.DISTRIBUTION,
		label: 'Delivery management',
		description:
			'Routes, trips, dispatch, Live Board, drivers, vehicles, replenishment, Schedule, dispatch settings',
		group: 'Operations',
		dependsOn: [],
	},
	{
		alias: Capabilities.FLEET,
		label: 'Fleet management',
		description:
			'DOT pre-trip and post-trip inspections, vehicle map, Motive telematics',
		group: 'Operations',
		dependsOn: [Capabilities.DISTRIBUTION],
	},
	{
		alias: Capabilities.EQUIPMENT,
		label: 'Equipment management',
		description:
			'Merchandisers, templates, merchandiser map, sensors, module faults and alerts, firmware, equipment drop-off and pick-up orders',
		group: 'Operations',
		dependsOn: [Capabilities.DISTRIBUTION],
	},
	{
		alias: Capabilities.SERVICE,
		label: 'Service management',
		description:
			'Service orders, service trips, service technicians, Schedule a fix, client Service tab',
		group: 'Operations',
		dependsOn: [Capabilities.DISTRIBUTION],
	},
	{
		alias: Capabilities.FIELD_SALES,
		label: 'Field sales routing',
		description: 'Sales routes, sales trips, named locations, My sales route',
		group: 'Operations',
		dependsOn: [],
	},
	{
		alias: Capabilities.MARKETING,
		label: 'Marketing',
		description: 'Email marketing campaigns, plus forms and the media library',
		group: 'Marketing and commerce',
		dependsOn: [],
	},
	{
		alias: Capabilities.WEB,
		label: 'Websites',
		description: 'Public websites, plus forms and the media library',
		group: 'Marketing and commerce',
		dependsOn: [],
	},
	{
		alias: Capabilities.ECOMMERCE,
		label: 'Ecommerce',
		description:
			'Ecommerce users, storefront sites (Brands & storefronts, storefront designs), plus forms and the media library',
		group: 'Marketing and commerce',
		dependsOn: [],
	},
	{
		alias: Capabilities.PROMOTIONS,
		label: 'Promotions',
		description: 'Promotions and the promotion engine',
		group: 'Marketing and commerce',
		dependsOn: [],
	},
	{
		alias: Capabilities.EMAIL,
		label: 'Email',
		description: 'Email mailbox, templates',
		group: 'Communications',
		dependsOn: [],
	},
	{
		alias: Capabilities.PHONE,
		label: 'Phone',
		description:
			'Calls, call history, AI phone assignments, click-to-dial integrations',
		group: 'Communications',
		dependsOn: [],
	},
	{
		alias: Capabilities.SMS,
		label: 'SMS',
		description:
			'Texting from the inbox, Send SMS, SMS agents, text sender numbers',
		group: 'Communications',
		dependsOn: [],
	},
	{
		alias: Capabilities.SOFTPHONE,
		label: 'Softphone',
		description:
			'In-browser phone system: communications inbox, live call monitor, coaching, voicemail, E911, port-in, Phone System settings',
		group: 'Communications',
		dependsOn: [Capabilities.PHONE],
	},
	{
		alias: Capabilities.COMPLIANCE,
		label: 'License management',
		description: `License registry, compliance dashboard, Needs Attention, match review, federal exclusions, ${LeadFinderDisplayName}, licensing jobs, checkout good standing, order-day re-verification`,
		group: 'Compliance',
		dependsOn: [],
	},
	{
		alias: Capabilities.PHARMACY,
		label: 'Pharmacy sales',
		description:
			'NPI, NCPDP, NDC and DEA schedule fields; State Board, DEA and controlled-substance order checks; Schedule II online ordering',
		group: 'Compliance',
		dependsOn: [],
	},
	{
		alias: Capabilities.AI_AGENTS,
		label: 'AI Agents',
		description: 'Agent setup, conversations, usage, monitoring, Sales Helper',
		group: 'AI',
		dependsOn: [],
	},
	{
		alias: Capabilities.AI_MCP,
		label: 'Claude Connector',
		description:
			'Lets users connect Claude to the CRM through the MCP connector',
		group: 'AI',
		dependsOn: [],
	},
	{
		alias: Capabilities.CAMPAIGNS,
		label: 'Campaigns',
		description:
			'Email campaigns and adding contacts to campaigns. Off for customers until bulk-send limits are in place',
		group: 'Marketing and commerce',
		dependsOn: [Capabilities.MARKETING],
	},
	{
		alias: Capabilities.JOBS,
		label: 'Jobs and projects',
		description:
			'Jobs (won work delivered to a customer, with its orders, quotes and invoices) and projects (the construction project several customers bid on)',
		group: 'Sales and inventory',
		dependsOn: [Capabilities.SALES],
	},
];

/**
 * Predefined capability tier aliases.
 * These must match the `alias` column in the `_capability_tier` table.
 */
export const CapabilityTiers = {
	AI_ONLY: 'ai_only',
	BASIC: 'basic',
	STANDARD: 'standard',
	PREMIUM: 'premium',
} as const;

export type CapabilityTierAlias =
	(typeof CapabilityTiers)[keyof typeof CapabilityTiers];

/**
 * Default capability sets for each tier. The RemapPlanCapabilities core
 * migration writes the same memberships to `_capability_tier__capability`.
 * SMS joins every tier that has phone, because phone carried SMS until
 * PD-16917 split it out.
 */
export const DefaultTierCapabilities: Record<
	CapabilityTierAlias,
	ReadonlyArray<CapabilityAlias>
> = {
	[CapabilityTiers.AI_ONLY]: [
		Capabilities.AI_AGENTS,
		Capabilities.PHONE,
		Capabilities.SMS,
	],
	[CapabilityTiers.BASIC]: [
		Capabilities.CRM_CORE,
		Capabilities.SALES,
		Capabilities.REPORTING,
		Capabilities.PHONE,
		Capabilities.SMS,
	],
	[CapabilityTiers.STANDARD]: [
		Capabilities.CRM_CORE,
		Capabilities.SALES,
		Capabilities.INVENTORY,
		Capabilities.REPORTING,
		Capabilities.GOALS,
		Capabilities.PHONE,
		Capabilities.SMS,
		Capabilities.EMAIL,
		Capabilities.ECOMMERCE,
		Capabilities.MARKETING,
		Capabilities.WEB,
	],
	[CapabilityTiers.PREMIUM]: [
		Capabilities.CRM_CORE,
		Capabilities.SALES,
		Capabilities.INVENTORY,
		Capabilities.DISTRIBUTION,
		Capabilities.FLEET,
		Capabilities.FIELD_SALES,
		Capabilities.AI_AGENTS,
		Capabilities.REPORTING,
		Capabilities.AUTOMATION,
		Capabilities.EMAIL,
		Capabilities.ECOMMERCE,
		Capabilities.GOALS,
		Capabilities.PHONE,
		Capabilities.SMS,
		Capabilities.MARKETING,
		Capabilities.WEB,
	],
};

/**
 * The strict capability rule: the instance holds the capability. There is no
 * "nothing loaded means everything" fallback (owner decision 2026-10-08); a
 * caller that has not loaded the set yet gets false, never a guess.
 */
export function isCapabilityAllowed(
	capabilities: ReadonlySet<string> | ReadonlyArray<string> | null | undefined,
	alias: string,
): boolean {
	if (capabilities == null) {
		return false;
	}

	if (Array.isArray(capabilities)) {
		return capabilities.includes(alias);
	}

	return (capabilities as ReadonlySet<string>).has(alias);
}

/** True when the instance holds ANY of the capabilities. */
export function isAnyCapabilityAllowed(
	capabilities: ReadonlySet<string> | ReadonlyArray<string> | null | undefined,
	aliases: ReadonlyArray<string>,
): boolean {
	return aliases.some((alias) => isCapabilityAllowed(capabilities, alias));
}

/**
 * The capabilities that gate an RBAC action: the tenant needs ANY one of them.
 * Returns null when no capability gates the action (core and system actions).
 * The longest mapped prefix wins, so 'integrations:mcp' overrides 'integrations'.
 */
export function getRequiredCapabilities(
	actionString: string,
): ReadonlyArray<CapabilityAlias> | null {
	const parts = actionString.split(':');

	if ((parts[0] ?? '') === '') {
		return null;
	}

	for (let length = parts.length; length > 0; length--) {
		const prefix = parts.slice(0, length).join(':');
		if (Object.prototype.hasOwnProperty.call(ActionCapabilityMap, prefix)) {
			const mapped = ActionCapabilityMap[prefix];
			if (mapped == null) {
				return null;
			}
			return typeof mapped === 'string' ? [mapped] : mapped;
		}
	}

	return null;
}

/**
 * Filter an array of RBAC action strings to only those whose capabilities
 * are present in the given capability set.
 *
 * Actions that don't map to any capability are always allowed through
 * (e.g., system-level actions like settings, user management). The rule is
 * strict: an empty set removes every mapped action, because an instance with
 * no capability rows holds no capabilities (PD-16917).
 */
export function filterActionsByCapabilities(
	actions: Array<string>,
	capabilities: ReadonlySet<string>,
): Array<string> {
	return actions.filter((action) => {
		const requiredCapabilities = getRequiredCapabilities(action);

		// No capability requirement — allow through (system actions, settings, etc.)
		if (requiredCapabilities == null) {
			return true;
		}

		return requiredCapabilities.some((capability) =>
			capabilities.has(capability),
		);
	});
}
